import { useEffect } from 'react';
import { FnITabContainer } from './CreditBureauForm.style';
import CreditBureauActivity from '../creditBureauActivity/CreditBureauActivity';
import { dealLockHooks } from '../hooks';

export const CreditBureauForm = () => {
    const isLocked = dealLockHooks.useIsDealLocked();

    useEffect(() => {
        let detailsObserver: MutationObserver | undefined;

        const applyDisabledStyles = (element: HTMLElement, includeVisualStyles: boolean = true) => {
            element.setAttribute('disabled', 'true');
            element.style.cursor = 'not-allowed';

            if (includeVisualStyles) {
                element.style.opacity = '0.5';
                element.style.backgroundColor = '#E3E3E3';
                element.style.color = '#666666';
                element.style.borderColor = '#E3E3E3';
            }
        };

        const disableButtons = (shadowRoot: ShadowRoot) => {
            const buttons = shadowRoot.querySelectorAll('button');
            buttons.forEach((button: HTMLButtonElement) => {
                const buttonText = (button.textContent || '').toLowerCase();
                const shouldDisable = buttonText.includes('request') || buttonText.includes('bureau');

                if (shouldDisable) {
                    applyDisabledStyles(button);
                    button.style.pointerEvents = 'none';
                }
            });
        };

        const disableTextInputs = (shadowRoot: ShadowRoot) => {
            const inputs = shadowRoot.querySelectorAll('input:not([type="checkbox"]):not([type="radio"])');
            inputs.forEach((input) => {
                applyDisabledStyles(input as HTMLInputElement);
            });
        };

        const disableCheckboxes = (shadowRoot: ShadowRoot) => {
            const checkboxes = shadowRoot.querySelectorAll('input[type="checkbox"]');
            checkboxes.forEach((checkbox) => {
                applyDisabledStyles(checkbox as HTMLInputElement, false);
            });
        };

        const disableSelects = (shadowRoot: ShadowRoot) => {
            const selects = shadowRoot.querySelectorAll('select');
            selects.forEach((select: HTMLSelectElement) => {
                applyDisabledStyles(select);
            });
        };

        const disableButtonsInShadowDOM = (shadowRoot: any) => {
            if (!shadowRoot || !isLocked) return;

            disableButtons(shadowRoot);
            disableTextInputs(shadowRoot);
            disableCheckboxes(shadowRoot);
            disableSelects(shadowRoot);
        };

        const injectNestedStyle = (details: Element) => {
            if (details && 'shadowRoot' in details && (details as any).shadowRoot) {
                // Remove ml-20 class from all elements inside the shadow root
                const rowsWithMl20 = (details as any).shadowRoot.querySelectorAll('.row.ml-20');
                rowsWithMl20.forEach((el: Element) => {
                    el.classList.remove('ml-20');
                });
                // Remove old style if already injected
                const oldStyle = (details as any).shadowRoot.querySelector('#custom-nested-style');
                if (oldStyle) oldStyle.remove();
                // Inject new style
                const nestedStyle = document.createElement('style');
                nestedStyle.id = 'custom-nested-style';
                nestedStyle.textContent =
                    '.col-12, .col-xl-5, .col-xxl-9, .col-xxl-3, .col-sm-6 { width: 100% !important; max-width: 100% !important; flex: 0 0 100% !important; } ' +
                    '.bp-section{margin-left: 0% !important}';
                (details as any).shadowRoot.appendChild(nestedStyle);

                // Disable buttons if deal is locked
                disableButtonsInShadowDOM((details as any).shadowRoot);

                if (detailsObserver) detailsObserver.disconnect();
            }
        };

        const tryInject = () => {
            const widget = document.querySelector('.fni-credit-bureau-tab-container fni-credit-bureau-widget');
            if (widget && 'shadowRoot' in widget && (widget as any).shadowRoot) {
                // Inject title styles directly into widget shadow root
                const titleStyle = document.createElement('style');
                titleStyle.id = 'title-mobile-style';
                titleStyle.textContent =
                    '@media (max-width: 768px) { .title { font-size: 18px !important; font-weight:600 !important; margin: 0px !important} }';
                (widget as any).shadowRoot.appendChild(titleStyle);
                let details = (widget as any).shadowRoot.querySelector('credit-bureau-widget-details, credit-bureau-request');

                if (details) {
                    injectNestedStyle(details);
                } else {
                    detailsObserver = new MutationObserver(() => {
                        details = (widget as any).shadowRoot.querySelector('credit-bureau-widget-details, credit-bureau-request');
                        if (details) {
                            injectNestedStyle(details);
                        }
                    });
                    (widget as any).shadowRoot &&
                        detailsObserver.observe((widget as any).shadowRoot, { childList: true, subtree: true });
                }
            }
        };

        tryInject();

        // Fallback observer in case widget loads late
        const observer: MutationObserver | undefined = new MutationObserver(() => {
            tryInject();
        });
        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            if (observer) observer.disconnect();
            if (detailsObserver) detailsObserver.disconnect();
        };
    }, [isLocked]);

    return (
        <FnITabContainer className="fni-credit-bureau-tab-container" data-testid="credit-bureau-form">
            <CreditBureauActivity />
        </FnITabContainer>
    );
};
